import React, { CSSProperties, ReactNode, RefObject } from 'react';
import ReactMapGL, {
  AttributionControl,
  MapEvent,
  MapLayerMouseEvent,
  MapProps,
  MapProvider,
  MapRef,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
// Static import: react-map-gl's maplibre entry defaults to a module-scope
// `import('maplibre-gl')` async chunk; passing mapLib explicitly keeps maplibre in the
// main bundle (same loading behavior as the previous static mapbox-gl import).
import maplibregl from 'maplibre-gl';
import { MapLoadIcon } from '@restworld/utils-common';

const TRUE = true;
// OpenFreeMap vector tiles (OpenMapTiles schema): free, keyless, commercial use allowed.
// Providers with the same schema (MapTiler, self-hosted PMTiles) are drop-in fallbacks by
// swapping this URL only.
export const DEFAULT_MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

export const MapWrapper = ({
  children,
  mapRef,
  coordinates,
  zoom,
  handleZoomEnd,
  mapStyle = DEFAULT_MAP_STYLE,
  initialViewState,
  touchZoomRotate = TRUE,
  cooperativeGestures = TRUE,
  onMove,
  onDrag,
  onResize,
  onZoom,
  style,
  interactiveLayerIds,
  onClick,
  viewState,
  reuseMaps = false,
  onLoad,
  scrollZoom = true,
  dragRotate = true,
  dragPan = true,
  doubleClickZoom = true,
  ...rest
}: MapWrapperProps) => {
  return (
    <MapProvider>
      <ReactMapGLStyled
        ref={mapRef}
        mapLib={maplibregl}
        // No RTL scripts needed for an Italy-only product: the default would fetch the
        // rtl-text plugin from the Mapbox CDN on every map load.
        RTLTextPlugin=""
        initialViewState={initialViewState}
        onZoomEnd={handleZoomEnd}
        mapStyle={mapStyle}
        touchZoomRotate={touchZoomRotate}
        cooperativeGestures={cooperativeGestures}
        onMove={onMove}
        onDrag={onDrag}
        onResize={onResize}
        // OSM/OpenFreeMap attribution is required by the data license (ODbL). Rendered
        // compact (bottom-left) as an "i" that expands on tap. Never hide it via CSS.
        attributionControl={false}
        style={style}
        interactiveLayerIds={interactiveLayerIds}
        onClick={onClick}
        onZoom={onZoom}
        reuseMaps={reuseMaps}
        viewState={viewState}
        onLoad={onLoad}
        scrollZoom={scrollZoom}
        dragRotate={dragRotate}
        dragPan={dragPan}
        doubleClickZoom={doubleClickZoom}
        {...rest}
      >
        <AttributionControl compact position="bottom-left" />
        {children}
      </ReactMapGLStyled>
    </MapProvider>
  );
};
export default MapWrapper;
const ReactMapGLStyled = React.forwardRef<MapRef, React.ComponentProps<typeof ReactMapGL>>((props, ref) => (
  <ReactMapGL ref={ref} {...props} style={{ position: 'relative', ...props.style }} />
));
ReactMapGLStyled.displayName = 'ReactMapGLStyled';
type MapEventHandlerType = (e: ViewStateChangeEvent) => void;
export interface MapWrapperProps {
  coordinates?: number[];
  children?: ReactNode;
  mapRef?: RefObject<MapRef | null>;
  zoom?: number;
  handleZoomEnd?: MapEventHandlerType;
  mapStyle?: string;
  touchZoomRotate?: boolean;
  cooperativeGestures?: boolean;
  longitude?: number;
  latitude?: number;
  onMove?: MapEventHandlerType;
  onDrag?: MapEventHandlerType;
  onZoom?: MapEventHandlerType;
  onResize?: (e: MapEvent) => void;
  style?: CSSProperties;
  initialViewState?: MapProps['initialViewState'];
  interactiveLayerIds?: string[];
  onClick?: (e: MapLayerMouseEvent) => void;
  viewState?: ViewState & {
    width: number;
    height: number;
  };
  reuseMaps?: boolean;
  onLoad?: (e: MapEvent) => void;
  scrollZoom?: boolean;
  dragRotate?: boolean;
  dragPan?: boolean;
  doubleClickZoom?: boolean;
}
export const MapScriptLoadingUI = () => (
  <div className="absolute w-full h-full bg-gray-200 flex justify-center items-center">
    <div className="flex flex-col gap-2 items-center w-1/2">
      <span>
        <MapLoadIcon />
      </span>
      <span className="text-gray-500 font-semibold text-lg line-clamp-2 text-center md:w-1/2">
        Stiamo facendo girare il mappamondo...
      </span>
    </div>
  </div>
);
